import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';
import { filterLocationSuggestions } from '../../utils/constants';

const ZIPCODE_PATTERN = /^\d{5}$|^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const splitZipcodeInput = (input: string): string[] =>
  input
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

const isZipcodeInput = (input: string): boolean => {
  const parts = splitZipcodeInput(input);
  return parts.length > 0 && parts.every((p) => ZIPCODE_PATTERN.test(p));
};

const parseZipcodes = (input: string): string[] =>
  splitZipcodeInput(input).filter((z) => ZIPCODE_PATTERN.test(z));

const LocationsStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  isLoading,
  error,
}) => {
  const [input, setInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState<Array<{ name: string; type: string }>>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter states based on input
  useEffect(() => {
    const filtered = filterLocationSuggestions(input, data.locations);
    setFilteredStates(filtered);
  }, [input, data.locations]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddLocation = (location: string) => {
    const trimmed = location.trim();
    if (!trimmed) {
      setLocalError('Please enter a location');
      return;
    }

    if (data.locations.includes(trimmed)) {
      setLocalError('This location has already been added');
      return;
    }

    onChange({ ...data, locations: [...data.locations, trimmed] });
    setInput('');
    setShowDropdown(false);
    setLocalError(null);
  };

  const handleRemoveLocation = (location: string) => {
    const updated = data.locations.filter((loc) => loc !== location);
    onChange({ ...data, locations: updated });
    setLocalError(null);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setLocalError(null);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = input.trim();
      if (isZipcodeInput(trimmed)) {
        const zipcodes = Array.from(new Set(parseZipcodes(trimmed)));
        const newZipcodes = zipcodes.filter((z) => !data.locations.includes(z));
        if (newZipcodes.length > 0) {
          onChange({ ...data, locations: [...data.locations, ...newZipcodes] });
          setInput('');
          setShowDropdown(false);
          setLocalError(null);
        } else {
          setLocalError('These zip codes have already been added');
        }
      } else if (filteredStates.length > 0) {
        handleStateSelect(filteredStates[0]);
      } else {
        setLocalError('Please select a location from the list');
      }
    }
  };

  const handleStateSelect = (state: { name: string; type: string }) => {
    handleAddLocation(state.name);
  };

  const handleSubmit = async () => {
    if (data.locations.length === 0) {
      setLocalError('Please add at least one location');
      return;
    }

    const isValid = await validation(data);
    if (isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError('Please add at least one location');
    }
  };

  return (
    <div className="space-y-6">
      {/* Tag Input with Dropdown */}
      <div className="relative w-full max-w-[508px]" ref={dropdownRef}>
        <input
          type="text"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyPress}
          placeholder="Select a location"
          className="hover:bg-gray-100 text-sm text-gray-700 w-full max-w-[508px] text-body-text-large text-pdf-font font-paragraph border border-border-gray rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 px-4 py-2 cursor-pointer"
        />

        {/* Location Dropdown */}
        {showDropdown && !isZipcodeInput(input) && (
          <div className="absolute z-10 mt-1 w-full max-w-[508px] bg-white rounded-md shadow-md max-h-[200px] overflow-y-auto border border-gray-200">
            {filteredStates.length > 0 ? (
              filteredStates.map((state, index) => (
                <div
                  key={`${state.name}-${state.type}-${index}`}
                  role="option"
                  tabIndex={-1}
                  onMouseDown={() => handleStateSelect(state)}
                  className="px-4 py-2 text-body-text font-paragraph justify-center bg-white dark:text-black dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-600 w-full cursor-pointer transition-colors duration-300 first:rounded-t-md last:rounded-b-md"
                >
                  {state.name}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-body-text dark:text-black text-center text-gray-500">
                No location found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {/* {showSuggestions && (
        <div className="mb-[28px]">
          <p className="flex items-center text-heading-4 font-paragraph mb-4 text-pdf-font">
            Suggestions
          </p>
          <Suggestions
            suggestions={SUGGESTIONS_MAP.Locations}
            onSuggestionClick={handleSuggestionClick}
            layout="default"
          />
        </div>
      )} */}

      {/* Added Locations Display */}
      {data.locations.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 max-h-[250px] overflow-y-auto font-paragraph text-heading-4">
          {data.locations.map((location) => (
            <div key={location} className="flex items-center m-1 max-w-full">
              <div className="bg-primary text-white rounded-l-[4px] px-3 py-1 flex items-center max-w-[450px] truncate">
                {location}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveLocation(location)}
                className="bg-primary text-white rounded-r-md ml-[2px] px-[4px] py-1 flex items-center hover:bg-red-700 transition-colors"
              >
                <XIcon className="w-3 " />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || data.locations.length === 0}
        />
      </div>
    </div>
  );
};

// Import XIcon from the UI library
import { XIcon } from '@commander/ui';

export default LocationsStep;
